// src/pages/CustomerManagement.tsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';
// Import useCustomerContext hook
import { useCustomerContext } from '../context/customerContext';
import { Customer } from '../utils/types';
import { useNavigate } from 'react-router-dom';
import { hasRole } from '../utils/auth';
import { fetchRolesByUserId, fetchCustomer, createUser, createCustomer } from '../utils/api'; // Import createUser, createCustomer
import CustomerProfileModal from '../components/CustomerProfileModal'; // Import the profile modal for editing
import CreateCustomerModal from '../components/CreateCustomerModal'; // Import the new modal
import { fetchAvailableRoles, setUserRole } from '../utils/api'; // Add these imports


const ITEMS_PER_PAGE = 12;

interface CustomerDetailModalProps {
    customer: Customer | null;
    onClose: () => void;
    onSelectAndNavigate: (customer: Customer, role: string) => void; // For navigation after selection
}

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({ customer, onClose, onSelectAndNavigate }) => {
    const [roles, setRoles] = useState<any[]>([]);
    const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
    const [rolesError, setRolesError] = useState<string | null>(null);

    // --- Get the setter function from context ---
    const { setSelectedSessionCustomer } = useCustomerContext();

    const [availableRoles, setAvailableRoles] = useState<string[]>([]);
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [savingRole, setSavingRole] = useState(false);
    const [roleSaveError, setRoleSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (customer?.user_id) {
            fetchAvailableRoles()
                .then(setAvailableRoles)
                .catch(() => setAvailableRoles([]));
        }
    }, [customer]);

    useEffect(() => {
        // Set default selected role to current role
        if (roles.length > 0 && typeof roles[0] === 'object' && Array.isArray(roles[0].roles) && roles[0].roles.length > 0) {
            setSelectedRole(roles[0].roles[0]);
        }
    }, [roles]);

    const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedRole(e.target.value);
    };

    const handleSaveRole = async () => {
        if (!customer?.user_id || !selectedRole) return;
        setSavingRole(true);
        setRoleSaveError(null);
        try {
            await setUserRole(customer?.user_id, selectedRole);
            // Optionally refresh roles or show a success message
            setRoles([{ user: { ...customer }, roles: [selectedRole] }]);
        } catch (err: any) {
            setRoleSaveError('Failed to set role.');
        } finally {
            setSavingRole(false);
        }
    };


    useEffect(() => {
        if (customer?.user_id) {
            const fetchRoles = async () => {
                setLoadingRoles(true);
                setRolesError(null);
                setRoles([]);
                try {
                    const rolesData = await fetchRolesByUserId(customer?.user_id);
                    // console.log('roles', rolesData); // Debugging

                    // --- Adjust based on your actual API response structure ---
                    // Assuming rolesData is like { user: {...}, roles: ["role_name"] }
                    if (rolesData && typeof rolesData === 'object' && rolesData.user && Array.isArray(rolesData.roles)) {
                        setRoles([rolesData]); // Store the whole object if needed
                    } else if (Array.isArray(rolesData)) {
                        // Handle if API returns just an array of role objects/strings
                        setRoles(rolesData);
                    } else {
                        setRoles([]); // Fallback for unexpected structure
                    }
                    // --- End structure assumption ---

                } catch (err: any) {
                    console.error(`Error fetching roles for user ${customer?.user_id}:`, err);
                    setRolesError('Failed to fetch roles.');
                } finally {
                    setLoadingRoles(false);
                }
            };
            fetchRoles();
        } else {
            setRoles([]);
            setLoadingRoles(false);
            setRolesError(null);
        }
    }, [customer]);

    // --- Handler for selecting the customer ---
    const handleSelectClick = () => {
        if (customer) {
            let customerRole: string = 'dealer'; // Default role if fetch fails or no role found

            // Extract the role from the fetched roles state
            // Adjust this logic based on the actual structure of your 'roles' state
            if (roles.length > 0 && typeof roles[0] === 'object' && Array.isArray(roles[0].roles) && roles[0].roles.length > 0) {
                // Assuming roles state is like [{ user: {...}, roles: ["role_name"] }]
                customerRole = roles[0].roles[0]; // Get the first role
            } else if (roles.length > 0 && typeof roles[0] === 'string') {
                // Assuming roles state is just an array of role strings
                customerRole = roles[0];
            }
            // Add more conditions if your roles data structure is different

            // Create the customer object *with* the role included
            const customerToSelect: Customer = {
                ...customer,
                role: customerRole, // Add the determined role here
            };

            // console.log("Selecting customer with role:", customerToSelect); // For debugging

            setSelectedSessionCustomer(customerToSelect); // Set the enriched customer object globally
            onSelectAndNavigate(customerToSelect, customerRole); // Call the new prop
            // onClose(); // onClose will be called by onSelectAndNavigate or similar logic
        }
    };

    if (!customer) return null;

    // Helper to render role info - adjust based on final roles state structure
    const renderRoleInfo = (roleData: any) => {
        // Assuming roleData is the object like { user: {...}, roles: ["role_name"] }
        if (typeof roleData === 'object' && roleData.user && Array.isArray(roleData.roles)) {
            return (
                <div className="flex justify-between items-center">
                    <div>
                        <span>UID: {roleData.user.id}</span>
                        &nbsp;-&nbsp;
                        <span>{roleData.user.name?.toUpperCase()}</span>
                        &nbsp;-&nbsp;
                        <span className='text-pink-700 font-medium'>{roleData.roles[0]?.toUpperCase() || 'N/A'}</span>
                    </div>
                    {/* --- Make "Select" clickable --- */}
                    <button
                        onClick={handleSelectClick}
                        className="text-white py-1 px-2 rounded bg-green-600 hover:bg-green-800 font-semibold cursor-pointer  ml-4" // Added margin
                    >
                        Select this Customer
                    </button>
                </div>
            );
        }
        // Add fallback rendering if roles state holds just strings or other structures
        return <span className="text-gray-500">Role data format unclear.</span>;
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4">Select Customer to Act As</h2>
                <div className="space-y-2 text-sm">
                    <p><strong>ID:</strong> {customer?.id}</p>
                    <p><strong>Name:</strong> {customer?.name || 'N/A'}</p>
                    <p><strong>Email:</strong> {customer?.email || 'N/A'}</p>
                    <p><strong>Phone:</strong> {customer?.phone || 'N/A'}</p>
                    <p><strong>City:</strong> {customer?.city || 'N/A'}</p>

                    <div>
                        <label className="font-semibold">Select Role:</label>
                        <select
                            value={selectedRole}
                            onChange={handleRoleChange}
                            className="ml-2 border rounded px-2 py-1"
                        >
                            {availableRoles.map(role => (
                                <option key={role} value={role}>{role}</option>
                            ))}
                        </select>
                        <button
                            onClick={handleSaveRole}
                            disabled={savingRole || !selectedRole}
                            className="ml-2 bg-blue-600 hover:bg-blue-800 text-white px-3 py-1 rounded"
                        >
                            {savingRole ? 'Saving...' : 'Save Role'}
                        </button>
                        {roleSaveError && <span className="text-red-500 ml-2">{roleSaveError}</span>}
                    </div>

                    <div className="mt-3 pt-3 border-t">
                        <p className="font-semibold mb-1">User Info & Role:</p>
                        {loadingRoles && <p className="text-gray-500">Loading user roles...</p>}
                        {rolesError && <p className="text-red-500">{rolesError}</p>}
                        {!loadingRoles && !rolesError && (
                            roles.length > 0
                                ? roles.map((roleInfo, index) => <div key={index}>{renderRoleInfo(roleInfo)}</div>)
                                : <span className="text-gray-500">No user/role info found.</span>
                        )}
                    </div>
                </div>

                <button
                    onClick={onClose}
                    className="mt-6 bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded w-full"
                >
                    Close
                </button>
            </div>
        </div>
    );
};


// --- CustomerManagement Component (No changes needed below this line for this feature) ---
const CustomerManagement = () => {
    const navigate = useNavigate();

    useEffect(() => {
        if (!hasRole(['super_admin', 'staff', 'technician'])) {
            navigate('/');
        }
    }, [navigate]);


    // const { customers, loading, error, refreshCustomers } = useCustomerContext();
    const { customers, loading, error, refreshCustomers, setSelectedSessionCustomer } = useCustomerContext();

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    // const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    // const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    // State for the customer profile editing modal
    const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    // State for the customer detail/select modal
    const [customerForDetail, setCustomerForDetail] = useState<Customer | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
    // State for the create customer modal
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [selectingCustomerId, setSelectingCustomerId] = useState<number | null>(null); // Store ID of customer being selected



    // --- Filtering Logic ---
    const filteredCustomers = useMemo(() => {
        if (!searchTerm) {
            return customers;
        }
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return customers.filter(customer =>
            customer?.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.first_name?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.last_name?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.email?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.phone?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.city?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer?.id?.toString().includes(lowerCaseSearchTerm)
        );
    }, [customers, searchTerm]);

    // --- Pagination Logic ---
    const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);

    const currentCustomers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredCustomers.slice(startIndex, endIndex);
    }, [filteredCustomers, currentPage]);

    // --- Event Handlers ---
    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    }, []);

    const handlePageChange = useCallback((page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    }, [totalPages]);

    const handleRefresh = useCallback(() => {
        refreshCustomers();
        setSearchTerm('');
        setCurrentPage(1);
    }, [refreshCustomers]);

    // --- Modal Handlers ---
    // const handleSelectCustomer = useCallback((customer: Customer) => {
    //     setSelectedCustomer(customer);
    //     setIsModalOpen(true);
    // Updated to open CustomerProfileModal for editing
    const handleEditCustomer = useCallback(async (customer: Customer) => {

        setCustomerToEdit(customer);
        setIsEditModalOpen(true);
    }, []);

    // const handleCloseModal = useCallback(() => {
    //     setIsModalOpen(false);
    //     setSelectedCustomer(null);
    // }, []);

    const handleCloseEditModal = useCallback(() => {
        setIsEditModalOpen(false);
        setCustomerToEdit(null);
        refreshCustomers(); // Refresh list after closing modal to see changes
    }, [refreshCustomers]);

    // Handlers for CustomerDetailModal (for selecting "act as")
    const handleOpenDetailModal = useCallback((customer: Customer) => {
        setCustomerForDetail(customer);
        setIsDetailModalOpen(true);
    }, []);

    const handleCloseDetailModal = useCallback(() => {
        setIsDetailModalOpen(false);
        setCustomerForDetail(null);
    }, []);

    const handleSelectAndNavigate = useCallback((customer: Customer, role: string) => {
        handleCloseDetailModal();
        navigate('/'); // Navigate to home page after selection
        // }, [navigate]); // Dependency should be navigate, not refreshCustomers
    }, [navigate, handleCloseDetailModal]);

    // Handlers for CreateCustomerModal
    const handleOpenCreateModal = () => {
        setIsCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const handleCreateCustomer = async (name: string, email: string, phone: string) => {
        // API call logic will be here
        const newUser = await createUser({ name, email, phone });
        if (newUser && newUser.id) {
            await createCustomer({ user_id: newUser.id, name, email, phone });
            refreshCustomers(); // Refresh the list
            // Modal will close itself on success via its own logic
        } else {
            throw new Error("Failed to get new user ID after creation.");
        }
    };


    // --- Render Logic ---
    if (loading) return <div className="container mx-auto p-4 text-center">Loading customers...</div>;
    if (error) return <div className="container mx-auto p-4 text-red-500">Error: {error}</div>;


    const handleSelectThisCustomerClick = async (customerToSelectOnClick: Customer) => {
        if (!customerToSelectOnClick || !customerToSelectOnClick.user_id) {
            console.error("Customer data or user_id is missing for selection.");
            // Optionally, show a toast error to the user
            // toast.error("Cannot select customer: missing required information.");
            return;
        }

        setSelectingCustomerId(customerToSelectOnClick.id); // Set the ID of the customer being selected

        try {
            const rolesData = await fetchRolesByUserId(customerToSelectOnClick.user_id);
            let customerRole: string = 'dealer'; // Default role

            // Extract role similar to CustomerDetailModal
            if (rolesData && typeof rolesData === 'object' && rolesData.user && Array.isArray(rolesData.roles) && rolesData.roles.length > 0) {
                customerRole = rolesData.roles[0];
            } else if (Array.isArray(rolesData) && rolesData.length > 0 && typeof rolesData[0] === 'string') {
                customerRole = rolesData[0];
            }

            const customerWithRole: Customer = {
                ...customerToSelectOnClick,
                role: customerRole,
            };

            setSelectedSessionCustomer(customerWithRole);
            navigate('/');
        } catch (error) {
            console.error("Error selecting customer or fetching roles:", error);
            // toast.error("Failed to select customer?. Please try again.");
        } finally {
            setSelectingCustomerId(null); // Clear the ID when done
        }
    };

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-3xl font-bold mb-6">Customer Management</h1>


            <div className="sticky top-12 left-2 right-2 z-10 border border-gray-400 rounded shadow-md bg-gray-50 p-2 flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="flex-grow sm:flex-grow-0 w-full lg:w-2/3">
                    <input
                        type="text"
                        placeholder="Search by name, email, phone, city, or ID..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="p-2 border rounded w-full focus:outline-none focus:ring focus:border-blue-300"
                    />
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button
                        onClick={handleOpenCreateModal}
                        className="bg-green-600 hover:bg-green-700 text-xs text-white font-bold py-2 px-4 rounded transition duration-200 w-full sm:w-auto"
                    >
                        New Customer +
                    </button>
                    <button
                        onClick={handleRefresh}
                        className="bg-blue-500 hover:bg-blue-700 text-xs text-white font-bold py-2 px-4 rounded transition duration-200 w-full sm:w-auto"
                    >
                        Refresh List
                    </button>
                </div>
            </div>

            {/* Customer List */}
            {filteredCustomers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-16">
                    {currentCustomers.map((customer) => (
                        <div key={customer?.id} className="bg-white p-4 rounded-lg shadow border border-gray-200 flex flex-col justify-between">
                            <div className="mb-3">
                                <h4 className="font-semibold text-lmdg mb-1 truncate" title={customer?.name || ''}>
                                    {customer?.name || 'N/A'}
                                </h4>
                                <p className="text-sm text-gray-600 truncate" title={customer?.email || ''}>
                                    <span className="font-medium">Email:</span> {customer?.email || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                    <span className="font-medium">Phone:</span> {customer?.phone || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                    <span className="font-medium">City:</span> {customer?.city || 'N/A'}
                                </p>
                                {/* <p className="text-sm  text-gray-500 mt-1">
                                    <span className="font-medium">CID:</span> {customer?.id}
                                </p>
                                <p className="text-sm  text-gray-500 mt-1">
                                    <span className="font-medium">UID:</span> {customer?.user_id}
                                </p> */}

                                <button
                                    onClick={() => handleOpenDetailModal(customer)}
                                    className="w-full bg-gray-100 hover:bg-gray-200 text-green-600 text-xs font-bold py-1 px-4 rounded transition duration-200 mt-2"
                                >
                                    More Settings...
                                </button>
                            </div>

                            <div className='flex flex-row gap-2'>
                                <button
                                    // onClick={() => handleOpenDetailModal(customer)}
                                    onClick={() => handleSelectThisCustomerClick(customer)}
                                    className={`w-full bg-green-500 hover:bg-green-700 text-white text-sm font-bold py-2 px-4 rounded transition duration-200 mt-2 ${selectingCustomerId === customer?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={selectingCustomerId === customer?.id || selectingCustomerId !== null} // Disable if this one is loading OR any other is loading
                                >

                                    {selectingCustomerId === customer?.id ? 'Acting...' : 'Act as this Customer'}

                                </button>
                                <button
                                    onClick={() => handleEditCustomer(customer)}
                                    className="w-full bg-indigo-500 hover:bg-indigo-700 text-white text-sm font-bold py-2 px-4 rounded transition duration-200 mt-2"
                                >
                                    Edit Profile
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-center text-gray-500 mt-6">
                    {searchTerm ? 'No customers found matching your search.' : 'No customers available.'}
                </p>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="sticky bottom-20 bg-gray-50 flex justify-center items-center mt-8">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-2 py-2 border rounded bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {filteredCustomers.length} Customers
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border rounded bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700 mt-2">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border rounded bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Render the Modal */}
            {isEditModalOpen && customerToEdit && (
                <CustomerProfileModal
                    initialCustomerData={customerToEdit}
                    onClose={handleCloseEditModal}
                />
            )}

            {isDetailModalOpen && customerForDetail && (
                <CustomerDetailModal
                    customer={customerForDetail}
                    onClose={handleCloseDetailModal}
                    onSelectAndNavigate={handleSelectAndNavigate}
                />
            )}

            {isCreateModalOpen && (
                <CreateCustomerModal
                    isOpen={isCreateModalOpen}
                    onClose={handleCloseCreateModal}
                    onCreateCustomer={handleCreateCustomer}
                />
            )}

        </div>
    );
};

export default CustomerManagement;
